import React, { useEffect, useMemo, useRef, useState } from 'react';
import PePapersDisclaimerGate from '../pe-papers/shared/PePapersDisclaimerGate';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { savePePapersArchiveDoc } from '../../services/pePapersArchiveService';
import useAppStore from '../../store/appStore';
import { usePersonalDataSelector } from '../../store/usePersonalDataSelector';
import { downloadHtmlAsPdf } from '../../utils/pe-papers/htmlDocumentPdfDownload';

/** Noble Slate — Vorschau/PDF/Druck (analog Abmahnung) */
const ZEUGNIS_PDF_CSS = `
  .av-a4-page {
    display: block !important;
    width: 100% !important;
    max-width: 210mm !important;
    margin-left: auto !important;
    margin-right: auto !important;
    box-sizing: border-box !important;
    font-family: Georgia, 'Times New Roman', serif !important;
    color: #0f172a !important;
    font-size: 12px !important;
    line-height: 1.48 !important;
    padding: 11mm 13mm !important;
    background: #ffffff !important;
  }
  .av-a4-page * { box-sizing: border-box !important; }
  .av-a4-page .pdf-letterhead {
    width: 100% !important;
    text-align: center !important;
    border-bottom: 2px solid #1e293b !important;
    margin: 0 0 12px !important;
    padding: 0 0 10px !important;
  }
  .av-a4-page .pdf-letterhead h1 {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 20px !important;
    font-weight: 900 !important;
    letter-spacing: 0.12em !important;
    text-transform: uppercase !important;
    color: #0f172a !important;
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
    margin: 0 0 3px !important;
    text-align: center !important;
  }
  .av-a4-page .pdf-letterhead h2 {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 10.5px !important;
    font-weight: 700 !important;
    letter-spacing: 0.16em !important;
    text-transform: uppercase !important;
    color: #b48a56 !important;
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
    margin: 0 !important;
    text-align: center !important;
  }
  .av-a4-page .az-ki-body {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    float: none !important;
  }
  .av-a4-page .az-ki-body * {
    max-width: 100% !important;
    box-sizing: border-box !important;
    float: none !important;
    position: static !important;
  }
  .av-a4-page .az-ki-body h1,
  .av-a4-page .az-ki-body h2,
  .av-a4-page .az-ki-body h3,
  .av-a4-page .az-ki-body h4 {
    display: block !important;
    width: 100% !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 11.5px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.05em !important;
    text-align: left !important;
    background: #1e293b !important;
    color: #f8fafc !important;
    border: none !important;
    border-radius: 0 !important;
    padding: 6px 10px !important;
    margin: 10px 0 8px !important;
  }
  .av-a4-page .az-ki-body p,
  .av-a4-page .az-ki-body li {
    display: block !important;
    width: 100% !important;
    font-family: Georgia, 'Times New Roman', serif !important;
    font-size: 12px !important;
    line-height: 1.48 !important;
    color: #1e293b !important;
    text-align: justify !important;
    margin: 0 0 8px !important;
    padding: 0 !important;
  }
  .av-a4-page .az-ki-body ul,
  .av-a4-page .az-ki-body ol {
    display: block !important;
    width: 100% !important;
    margin: 0 0 10px !important;
    padding-left: 18px !important;
  }
  .av-a4-page .az-ki-body table {
    width: 100% !important;
    max-width: 100% !important;
    border-collapse: collapse !important;
    table-layout: fixed !important;
    margin: 0 0 10px !important;
  }
  .av-a4-page .az-ki-body td,
  .av-a4-page .az-ki-body th {
    text-align: left !important;
    vertical-align: top !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
  }
  .av-a4-page .az-ki-body .print-break,
  .av-a4-page .az-ki-body .pepapers-print-break {
    display: none !important;
    page-break-before: auto !important;
    break-before: auto !important;
  }
  .az-sig-table {
    width: 100% !important;
    border-collapse: collapse !important;
    table-layout: fixed !important;
    margin-top: 24px !important;
    border: none !important;
  }
  .az-sig-table td {
    width: 50% !important;
    vertical-align: top !important;
    border: none !important;
    background: transparent !important;
    padding: 0 !important;
  }
  .az-sig-table p {
    text-align: left !important;
    margin: 0 !important;
    font-family: Arial, Helvetica, sans-serif !important;
  }
  .az-sig-line-soft {
    display: block !important;
    width: 200px !important;
    max-width: 100% !important;
    border: none !important;
    border-bottom: 2px solid #1e293b !important;
    margin-top: 0 !important;
    margin-bottom: 6px !important;
    height: 0 !important;
    padding: 0 !important;
    background: transparent !important;
  }
  @media print {
    @page { size: A4; margin: 0; }
    body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
    .av-a4-page {
      box-shadow: none !important;
      margin: 0 !important;
      max-width: none !important;
      width: 100% !important;
      min-height: 0 !important;
    }
  }
`;

const ZEUGNIS_PRINT_CSS = `
${ZEUGNIS_PDF_CSS}
html, body {
  margin: 0 !important;
  padding: 0 !important;
  background: #fff !important;
}
`;

const normalizeWhitespace = (text: string) => text.replace(/\s+/g, ' ').trim();

/** Plenks vor Apostroph: "Susi 's" → "Susi's" */
const fixApostrophePlenks = (text: string): string =>
    String(text || '')
        .replace(/(\p{L})\s+['´`’]\s*s\b/gu, "$1's")
        .replace(/(\p{L})\s+['´`’]/gu, "$1'");

const isOrtDatumClosingLine = (text: string): boolean => {
    const t = normalizeWhitespace(text);
    if (!t || t.length > 100) return false;
    if (/^ort,?\s*datum\s*:/i.test(t)) return true;
    if (/^\d{1,2}\.\d{1,2}\.\d{2,4}\.?$/.test(t)) return true;
    // „Klein Kleckersdorf, 03.10.2026“
    return /^[\p{L}0-9\s.\-'´`’]+,\s*\d{1,2}\.\d{1,2}\.\d{2,4}\.?$/u.test(t);
};

const isGrussClosingLine = (text: string): boolean => {
    const t = normalizeWhitespace(text);
    return /^(mit\s+freundlichen\s+gr[uü]ßen|hochachtungsvoll|freundliche\s+gr[uü]ße)[\s.,!]*$/i.test(t);
};

const isShortNameClosingLine = (text: string, salonHint = ''): boolean => {
    const t = normalizeWhitespace(text);
    if (!t || t.length > 70) return false;
    if (/[.!?]/.test(t)) return false;
    if (/\b(war|wurde|hat|sind|wird|als|beschäftigt|tätig|aufgaben)\b/i.test(t)) return false;
    if (t.split(/\s+/).length > 6) return false;
    const hint = normalizeWhitespace(salonHint);
    if (hint && t.toLowerCase() === hint.toLowerCase()) return true;
    // reine Firmen-/Namenszeile ohne Satzzeichen
    return /^[\p{L}0-9\s.\-'´`’&]+$/u.test(t);
};

/** Entfernt KI-Leak-Blöcke, doppelte Schlusszeilen + Capture-Artefakte */
const scrubArbeitszeugnisHtml = (html: string, salonNameHint = ''): string => {
    const raw = String(html || '').trim();
    if (!raw) return '';

    const isLeakHeadingText = (text: string) => {
        const t = normalizeWhitespace(text).toUpperCase();
        return /T[ÄA]TIGKEITEN/.test(t) && /AUFGABEN/.test(t);
    };

    const isDuplicateTitle = (text: string) => {
        const t = normalizeWhitespace(text).toUpperCase();
        return (
            t === 'ARBEITSZEUGNIS' ||
            t === 'AUSBILDUNGSZEUGNIS' ||
            t === 'EINFACHES ARBEITSZEUGNIS' ||
            t === 'QUALIFIZIERTES ARBEITSZEUGNIS'
        );
    };

    try {
        const doc = new DOMParser().parseFromString(`<div id="az-scrub-root">${raw}</div>`, 'text/html');
        const root = doc.getElementById('az-scrub-root');
        if (!root) return fixApostrophePlenks(raw);

        root.querySelectorAll('style, script').forEach((el) => el.remove());
        root.querySelectorAll('.print-break, .pepapers-print-break').forEach((el) => el.remove());

        const candidates = Array.from(root.querySelectorAll('h1,h2,h3,h4,h5,h6,p,div,strong'));
        for (const el of candidates) {
            if (!isLeakHeadingText(el.textContent || '')) continue;
            let node: ChildNode | null = el;
            while (node) {
                const next: ChildNode | null = node.nextSibling;
                const parent = node.parentNode;
                parent?.removeChild(node);
                if (!next) break;
                if (next.nodeType === 1) {
                    const tag = (next as Element).tagName;
                    if (/^H[1-6]$/.test(tag) && !isLeakHeadingText((next as Element).textContent || '')) {
                        break;
                    }
                }
                node = next;
            }
        }

        // Doppelten Dokumenttitel entfernen (Letterhead übernimmt das)
        const firstHeading = root.querySelector('h1, h2');
        if (firstHeading && isDuplicateTitle(firstHeading.textContent || '')) {
            firstHeading.remove();
        }

        // Doppelte KI-Schlusszeilen entfernen (Template hat Ort/Datum + Unterschrift)
        const isCombinedClosingBlock = (text: string) => {
            const lines = String(text || '')
                .replace(/<br\s*\/?>/gi, '\n')
                .split(/\n+/)
                .map((line) => normalizeWhitespace(line))
                .filter(Boolean);
            if (lines.length === 1) {
                // Reiner Firmenname allein nur über den getrennten Short-Name-Zweig (mit Kontext)
                return isOrtDatumClosingLine(lines[0]) || isGrussClosingLine(lines[0]);
            }
            if (lines.length === 2) {
                const [a, b] = lines;
                return (
                    (isOrtDatumClosingLine(a) || isGrussClosingLine(a)) &&
                    isShortNameClosingLine(b, salonNameHint)
                );
            }
            return false;
        };

        const blocks = Array.from(root.querySelectorAll('p')).filter((p) =>
            normalizeWhitespace(p.textContent || '')
        );
        let removedClosing = 0;
        while (blocks.length > 0 && removedClosing < 4) {
            const last = blocks[blocks.length - 1];
            const rawLast = last.textContent || '';
            const t = normalizeWhitespace(rawLast);
            if (isCombinedClosingBlock(rawLast) || isOrtDatumClosingLine(t) || isGrussClosingLine(t)) {
                last.remove();
                blocks.pop();
                removedClosing += 1;
                continue;
            }
            if (isShortNameClosingLine(t, salonNameHint)) {
                const prev = blocks[blocks.length - 2];
                const prevText = prev ? normalizeWhitespace(prev.textContent || '') : '';
                if (
                    removedClosing > 0 ||
                    isOrtDatumClosingLine(prevText) ||
                    isGrussClosingLine(prevText)
                ) {
                    last.remove();
                    blocks.pop();
                    removedClosing += 1;
                    continue;
                }
            }
            break;
        }

        root.querySelectorAll<HTMLElement>('[style]').forEach((el) => {
            el.style.removeProperty('float');
            el.style.removeProperty('position');
            el.style.removeProperty('left');
            el.style.removeProperty('right');
            el.style.removeProperty('top');
            el.style.removeProperty('bottom');
            el.style.removeProperty('transform');
            el.style.removeProperty('margin-left');
            el.style.removeProperty('margin-right');
            el.style.removeProperty('width');
            el.style.removeProperty('max-width');
            el.style.removeProperty('page-break-before');
            el.style.removeProperty('page-break-after');
            el.style.removeProperty('break-before');
            el.style.removeProperty('break-after');
            if (!el.getAttribute('style')?.trim()) el.removeAttribute('style');
        });

        const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        const textNodes: Text[] = [];
        let current = walker.nextNode();
        while (current) {
            textNodes.push(current as Text);
            current = walker.nextNode();
        }
        textNodes.forEach((node) => {
            node.textContent = fixApostrophePlenks(node.textContent || '');
        });

        return root.innerHTML.trim();
    } catch {
        return fixApostrophePlenks(
            raw
                .replace(/<style[\s\S]*?<\/style>/gi, '')
                .replace(
                    /<(h[1-6]|p|div|strong)[^>]*>\s*T[ÄA]TIGKEITEN\s+UND\s+AUFGABEN\s*<\/\1>[\s\S]*?(?=<h[1-6][^>]*>|$)/gi,
                    ''
                )
                .replace(/T[ÄA]TIGKEITEN\s+UND\s+AUFGABEN[\s\S]*?(?=<h[1-6]|$)/gi, '')
                .replace(
                    /<p[^>]*>\s*[\p{L}0-9\s.\-'´`’]+,\s*\d{1,2}\.\d{1,2}\.\d{2,4}\.?\s*<\/p>\s*$/giu,
                    ''
                )
                .trim()
        );
    }
};

/** Archiv kann volle Druck-Shell oder reinen KI-Body enthalten → immer KI-Fließtext extrahieren */
const extractKiHtmlFromStored = (stored: string, salonNameHint = ''): string => {
    const raw = String(stored || '').trim();
    if (!raw) return '';
    try {
        const doc = new DOMParser().parseFromString(`<div id="az-extract-root">${raw}</div>`, 'text/html');
        const root = doc.getElementById('az-extract-root');
        if (!root) return scrubArbeitszeugnisHtml(raw, salonNameHint);

        const kiBody = root.querySelector('.az-ki-body');
        if (kiBody) return scrubArbeitszeugnisHtml(kiBody.innerHTML, salonNameHint);

        const page = root.querySelector('.av-a4-page');
        if (page) {
            page.querySelectorAll('style, .pdf-letterhead, .az-sig-table').forEach((el) => el.remove());
            return scrubArbeitszeugnisHtml(page.innerHTML, salonNameHint);
        }

        return scrubArbeitszeugnisHtml(raw, salonNameHint);
    } catch {
        return scrubArbeitszeugnisHtml(raw, salonNameHint);
    }
};

interface ArbeitszeugnisModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialData?: any;
    onArchived?: () => void;
}

type ZeugnisTyp = 'einfach' | 'qualifiziert';

type BewertungsStufe =
    | ''
    | 'sehr_gut'
    | 'gut'
    | 'befriedigend'
    | 'ausreichend'
    | 'maessig';

type Austrittsgrund = '' | 'auf_eigenen_wunsch' | 'betriebsbedingt' | 'fristgerecht_arbeitgeber' | 'einvernehmen' | 'fristlos' | 'befristung_abgelaufen';

type Branche =
    | ''
    | 'friseurhandwerk'
    | 'kosmetikstudio'
    | 'nagelstudio'
    | 'wellness_spa'
    | 'fusspflege_podologie'
    | 'barbershop'
    | 'sonstiges';

const BRANCHE_OPTIONS: { key: Exclude<Branche, ''>; label: string }[] = [
    { key: 'friseurhandwerk', label: 'Friseurhandwerk' },
    { key: 'kosmetikstudio', label: 'Kosmetikstudio' },
    { key: 'nagelstudio', label: 'Nagelstudio / Nail-Design' },
    { key: 'wellness_spa', label: 'Wellness & Spa' },
    { key: 'fusspflege_podologie', label: 'Fußpflege / Podologie' },
    { key: 'barbershop', label: 'Barbershop' },
    { key: 'sonstiges', label: 'Sonstiges (eigene Branche eingeben)' },
];

const AUSTRITTSGRUND_OPTIONS: { key: Exclude<Austrittsgrund, ''>; label: string }[] = [
    { key: 'auf_eigenen_wunsch', label: 'auf eigenen Wunsch (Arbeitnehmer hat gekündigt)' },
    { key: 'betriebsbedingt', label: 'betriebsbedingt (Arbeitgeber hat gekündigt)' },
    { key: 'fristgerecht_arbeitgeber', label: 'fristgerecht durch den Arbeitgeber' },
    { key: 'einvernehmen', label: 'im gegenseitigen Einvernehmen (Aufhebungsvertrag)' },
    { key: 'fristlos', label: 'Fristlose Kündigung' },
    { key: 'befristung_abgelaufen', label: 'Befristung abgelaufen' },
];

const ZEUGNIS_TYP_OPTIONS: { key: ZeugnisTyp; label: string }[] = [
    { key: 'einfach', label: 'Einfaches Arbeitszeugnis' },
    { key: 'qualifiziert', label: 'Qualifiziertes Arbeitszeugnis' },
];

const BEWERTUNGS_STUFEN: { key: Exclude<BewertungsStufe, ''>; label: string }[] = [
    { key: 'sehr_gut', label: 'Sehr gut / stets zu unserer vollsten Zufriedenheit' },
    { key: 'gut', label: 'Gut / stets zur vollen Zufriedenheit' },
    { key: 'befriedigend', label: 'Befriedigend / zur vollen Zufriedenheit' },
    { key: 'ausreichend', label: 'Ausreichend / hat sich bewährt' },
    { key: 'maessig', label: 'Mäßig / war stets bemüht' },
];

const KI_KLARTEXT_HINWEIS =
    'Hinweis: Dein Klartext im „Fieser Textsack“ wird bei der Dokumentenerstellung automatisch per KI in rechtssichere Zeugnissprache übersetzt. Formuliere ehrlich und konkret – die KI übernimmt die diplomatische Übersetzung.';

const A4_PREVIEW_PAGE_CLASS =
    'av-a4-page bg-white text-slate-900 my-4 mx-auto w-full max-w-[210mm] h-auto min-h-0 shadow-xl rounded-sm text-left block overflow-visible print:my-0 print:shadow-none';

const KI_HTML_PREVIEW_CLASS = 'az-ki-body w-full h-auto overflow-visible';

const INPUT_CLASS =
    'w-full rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black text-pe-text dark:text-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#E6C299]/50 transition-colors';

const DATE_INPUT_CLASS = `${INPUT_CLASS} cursor-pointer arbeitszeugnis-date-input`;

const TEXTAREA_CLASS = `${INPUT_CLASS} min-h-[120px] resize-y`;

const FIESER_TEXTSACK_CLASS = `${INPUT_CLASS} min-h-[180px] resize-y`;

const LABEL_CLASS = 'text-xs font-semibold text-pe-muted dark:text-slate-400 uppercase tracking-wider mb-1 block';

const ERROR_INPUT_RING = 'border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500/20';

const STEP_LABELS: Record<number, string> = {
    1: 'Datenerfassung & Zeugnis-Typ',
    2: 'Vorschau & Export',
};

const API_ERROR_MESSAGE = 'Fehler bei der KI-Generierung. Bitte Eingaben prüfen.';

const isEmpty = (value: string) => !String(value || '').trim();

type TeamPool = 'produktiv' | 'unproduktiv' | 'azubi';

type TeamEmployeeOption = {
    id: string;
    name: string;
    pool: TeamPool;
};

/** „Max Mustermann“ → Vorname / Nachname (letzter Token = Nachname) */
const splitMitarbeiterName = (fullName: string): { vorname: string; nachname: string } => {
    const parts = String(fullName || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    if (parts.length === 0) return { vorname: '', nachname: '' };
    if (parts.length === 1) return { vorname: parts[0], nachname: '' };
    return {
        vorname: parts.slice(0, -1).join(' '),
        nachname: parts[parts.length - 1],
    };
};

const BEWERTUNG_TO_NUMBER: Record<Exclude<BewertungsStufe, ''>, number> = {
    sehr_gut: 1,
    gut: 2,
    befriedigend: 3,
    ausreichend: 4,
    maessig: 5,
};

const mapBewertungToNumber = (key: BewertungsStufe): number => {
    if (!key) return 0;
    return BEWERTUNG_TO_NUMBER[key];
};

const resolveBrancheLabel = (branche: Branche, brancheSonstiges: string): string => {
    if (branche === 'sonstiges') return String(brancheSonstiges || '').trim();
    const option = BRANCHE_OPTIONS.find((opt) => opt.key === branche);
    return option?.label || String(branche || '').trim();
};

const getCallableErrorMessage = (error: unknown): string => {
    if (error && typeof error === 'object') {
        const firebaseError = error as { code?: string; message?: string };
        const code = typeof firebaseError.code === 'string' ? firebaseError.code.trim() : '';
        const message = typeof firebaseError.message === 'string' ? firebaseError.message.trim() : '';
        if (code && message) return `${code}: ${message}`;
        if (message) return message;
        if (code) return code;
    }
    if (error instanceof Error && error.message.trim()) return error.message.trim();
    return '';
};

const logFirebaseCallableError = (error: unknown, payload: Record<string, unknown>) => {
    console.error('API-Fehler abgefangen:', error);
    console.error('generatePePapersArbeitszeugnis Payload (bei Fehler):', payload);

    if (error && typeof error === 'object') {
        const firebaseError = error as { code?: string; message?: string; details?: unknown };
        console.error('Firebase Callable Error Details:', {
            code: firebaseError.code ?? 'UNKNOWN',
            message: firebaseError.message ?? 'Keine Fehlermeldung',
            details: firebaseError.details ?? null,
        });
        return;
    }

    console.error('Firebase Callable Error Details:', {
        code: 'UNKNOWN',
        message: String(error),
        details: null,
    });
};

const ArbeitszeugnisModal: React.FC<ArbeitszeugnisModalProps> = ({
    isOpen,
    onClose,
    initialData,
    onArchived,
}) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [formError, setFormError] = useState('');
    const [zeugnisTyp, setZeugnisTyp] = useState<ZeugnisTyp>('einfach');
    const [istAuszubildender, setIstAuszubildender] = useState(false);
    const [selectedTeamEmployeeId, setSelectedTeamEmployeeId] = useState('');
    const [vorname, setVorname] = useState('');
    const [nachname, setNachname] = useState('');
    const [geburtsdatum, setGeburtsdatum] = useState('');
    const [geburtsort, setGeburtsort] = useState('');
    const [branche, setBranche] = useState<Branche>('');
    const [brancheSonstiges, setBrancheSonstiges] = useState('');
    const [position, setPosition] = useState('');
    const [aufgaben, setAufgaben] = useState('');
    const [eintrittsdatum, setEintrittsdatum] = useState('');
    const [austrittsdatum, setAustrittsdatum] = useState('');
    const [austrittsgrund, setAustrittsgrund] = useState<Austrittsgrund>('');
    const [ausstellungsdatum, setAusstellungsdatum] = useState('');
    const [leistungsbewertung, setLeistungsbewertung] = useState<BewertungsStufe>('');
    const [verhaltenVorgesetzte, setVerhaltenVorgesetzte] = useState<BewertungsStufe>('');
    const [verhaltenKollegen, setVerhaltenKollegen] = useState<BewertungsStufe>('');
    const [fieserTextsack, setFieserTextsack] = useState('');
    const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
    const [generatedHtml, setGeneratedHtml] = useState<string | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isPdfDownloading, setIsPdfDownloading] = useState(false);
    const [apiError, setApiError] = useState(false);
    const [generationTrigger, setGenerationTrigger] = useState(0);
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [archiveFeedback, setArchiveFeedback] = useState<'idle' | 'success' | 'error' | 'draft'>('idle');
    const [archiveDocId, setArchiveDocId] = useState<string | null>(null);

    const printContainerRef = useRef<HTMLDivElement>(null);
    const generationInFlightRef = useRef(false);
    const payloadSnapshotRef = useRef<Record<string, unknown> | null>(null);
    const hydrateKeyRef = useRef<string | null>(null);
    const clientData = useAppStore((state: any) => state.clientData);
    const { personalData, unproductivePersonalData, azubiAssistenzData } = usePersonalDataSelector();

    const teamEmployees = useMemo((): TeamEmployeeOption[] => {
        const mapPool = (list: any[], pool: TeamPool): TeamEmployeeOption[] =>
            (Array.isArray(list) ? list : [])
                .filter((emp) => emp?.id)
                .map((emp) => ({
                    id: String(emp.id),
                    name: String(emp.name || '').trim() || 'Ohne Name',
                    pool,
                }));
        return [
            ...mapPool(personalData, 'produktiv'),
            ...mapPool(unproductivePersonalData, 'unproduktiv'),
            ...mapPool(azubiAssistenzData, 'azubi'),
        ];
    }, [personalData, unproductivePersonalData, azubiAssistenzData]);

    const handleTeamEmployeeSelect = (employeeId: string) => {
        setSelectedTeamEmployeeId(employeeId);
        if (!employeeId) return;
        const emp = teamEmployees.find((e) => e.id === employeeId);
        if (!emp) return;
        const { vorname: v, nachname: n } = splitMitarbeiterName(
            emp.name === 'Ohne Name' ? '' : emp.name
        );
        setVorname(v);
        setNachname(n);
        clearFieldError('vorname');
        clearFieldError('nachname');
        if (emp.pool === 'azubi') {
            setIstAuszubildender(true);
        }
    };

    const resetForm = () => {
        setCurrentStep(1);
        setFormError('');
        setZeugnisTyp('einfach');
        setIstAuszubildender(false);
        setSelectedTeamEmployeeId('');
        setVorname('');
        setNachname('');
        setGeburtsdatum('');
        setGeburtsort('');
        setBranche('');
        setBrancheSonstiges('');
        setPosition('');
        setAufgaben('');
        setEintrittsdatum('');
        setAustrittsdatum('');
        setAustrittsgrund('');
        setAusstellungsdatum('');
        setLeistungsbewertung('');
        setVerhaltenVorgesetzte('');
        setVerhaltenKollegen('');
        setFieserTextsack('');
        setInvalidFields(new Set());
        setGeneratedHtml(null);
        setIsGenerating(false);
        setApiError(false);
        generationInFlightRef.current = false;
        payloadSnapshotRef.current = null;
        setGenerationTrigger(0);
        setDisclaimerAccepted(false);
        setIsSaving(false);
        setArchiveFeedback('idle');
        setArchiveDocId(null);
        hydrateKeyRef.current = null;
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    useEffect(() => {
        if (!isOpen) {
            resetForm();
            return;
        }

        // Nur einmal pro Öffnung/Archiv-Doc hydratisieren — sonst wird die ausgefüllte Branche
        // bei Parent-Rerenders weggewischt und die API meldet fälschlich „branche fehlt“.
        const hydrateKey = initialData?.id ? `doc:${String(initialData.id)}` : 'new';
        if (hydrateKeyRef.current === hydrateKey) return;
        hydrateKeyRef.current = hydrateKey;

        resetForm();
        hydrateKeyRef.current = hydrateKey;
        setArchiveDocId(initialData?.id || null);

        if (!initialData) return;

        if (initialData.zeugnisTyp === 'einfach' || initialData.zeugnisTyp === 'qualifiziert') {
            setZeugnisTyp(initialData.zeugnisTyp);
        }
        setIstAuszubildender(Boolean(initialData.istAuszubildender));
        if (typeof initialData.selectedTeamEmployeeId === 'string') {
            setSelectedTeamEmployeeId(initialData.selectedTeamEmployeeId);
        }
        if (typeof initialData.vorname === 'string') setVorname(initialData.vorname);
        if (typeof initialData.nachname === 'string') setNachname(initialData.nachname);
        if (typeof initialData.geburtsdatum === 'string') setGeburtsdatum(initialData.geburtsdatum);
        if (typeof initialData.geburtsort === 'string') setGeburtsort(initialData.geburtsort);
        if (typeof initialData.branche === 'string') setBranche(initialData.branche as Branche);
        if (typeof initialData.brancheSonstiges === 'string') {
            setBrancheSonstiges(initialData.brancheSonstiges);
        }
        if (typeof initialData.position === 'string') setPosition(initialData.position);
        if (typeof initialData.aufgaben === 'string') setAufgaben(initialData.aufgaben);
        if (typeof initialData.eintrittsdatum === 'string') setEintrittsdatum(initialData.eintrittsdatum);
        if (typeof initialData.austrittsdatum === 'string') setAustrittsdatum(initialData.austrittsdatum);
        if (typeof initialData.austrittsgrund === 'string') {
            setAustrittsgrund(initialData.austrittsgrund as Austrittsgrund);
        }
        if (typeof initialData.ausstellungsdatum === 'string') {
            setAusstellungsdatum(initialData.ausstellungsdatum);
        }
        if (typeof initialData.leistungsbewertung === 'string') {
            setLeistungsbewertung(initialData.leistungsbewertung as BewertungsStufe);
        }
        if (typeof initialData.verhaltenVorgesetzte === 'string') {
            setVerhaltenVorgesetzte(initialData.verhaltenVorgesetzte as BewertungsStufe);
        }
        if (typeof initialData.verhaltenKollegen === 'string') {
            setVerhaltenKollegen(initialData.verhaltenKollegen as BewertungsStufe);
        }
        if (typeof initialData.fieserTextsack === 'string') setFieserTextsack(initialData.fieserTextsack);
        if (initialData.confirmedDisclaimer) setDisclaimerAccepted(true);

        const salonHint = String(clientData?.salonname || clientData?.firmenname || '').trim();
        const restoredHtml = extractKiHtmlFromStored(
            typeof initialData.generatedHtml === 'string' ? initialData.generatedHtml : '',
            salonHint
        );
        const step = Number(initialData.currentStep);

        // Archivierter KI-Text hat Vorrang — kein erneuter Cloud-Call
        if (restoredHtml) {
            setGeneratedHtml(restoredHtml);
            setCurrentStep(2);
            setApiError(false);
            setGenerationTrigger(0);
        } else if (Number.isFinite(step) && step >= 1 && step <= 2) {
            setCurrentStep(step);
            if (step === 2) {
                const restoredBranche = String(initialData.branche || '') as Branche;
                const restoredBrancheSonstiges = String(initialData.brancheSonstiges || '');
                const brancheResolved =
                    resolveBrancheLabel(restoredBranche, restoredBrancheSonstiges) ||
                    'Friseur- und Kosmetiksalon';
                payloadSnapshotRef.current = {
                    branche: brancheResolved,
                    arbeitgeberDaten: {
                        salonname: clientData?.salonname || clientData?.firmenname || '',
                        ort: clientData?.ort || '',
                        strasse: clientData?.strasse || '',
                        plz: clientData?.plz || '',
                        branche: brancheResolved,
                    },
                    mitarbeiterDaten: {
                        vorname: String(initialData.vorname || '').trim(),
                        nachname: String(initialData.nachname || '').trim(),
                        geburtsdatum: String(initialData.geburtsdatum || ''),
                        geburtsort: String(initialData.geburtsort || '').trim(),
                        position: String(initialData.position || '').trim(),
                    },
                    zeitraum: {
                        eintrittsdatum: String(initialData.eintrittsdatum || ''),
                        austrittsdatum: String(initialData.austrittsdatum || ''),
                        ausstellungsdatum: String(initialData.ausstellungsdatum || ''),
                    },
                    aufgaben: String(initialData.aufgaben || '').trim(),
                    zeugnisTyp:
                        initialData.zeugnisTyp === 'qualifiziert' ? 'qualifiziert' : 'einfach',
                    istAuszubildender: Boolean(initialData.istAuszubildender),
                    austrittsgrund: String(initialData.austrittsgrund || ''),
                    ...(initialData.zeugnisTyp === 'qualifiziert'
                        ? {
                              klartextFeedback: String(initialData.fieserTextsack || '').trim(),
                              bewertungen: {
                                  leistung: mapBewertungToNumber(
                                      (initialData.leistungsbewertung || '') as BewertungsStufe
                                  ),
                                  verhaltenVorgesetzte: mapBewertungToNumber(
                                      (initialData.verhaltenVorgesetzte || '') as BewertungsStufe
                                  ),
                                  verhaltenKollegen: mapBewertungToNumber(
                                      (initialData.verhaltenKollegen || '') as BewertungsStufe
                                  ),
                              },
                          }
                        : {}),
                };
                setGenerationTrigger(1);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, initialData]);

    const buildApiPayload = (): Record<string, unknown> => {
        const brancheKey = branche;
        const brancheResolved =
            resolveBrancheLabel(branche, brancheSonstiges) || 'Friseur- und Kosmetiksalon';
        const payload: Record<string, unknown> = {
            // Backend: „branche fehlt“ bezog sich auf leeres/fehlendes top-level Feld —
            // bei Sonstiges kommt der Freitext, sonst das Label (nie der leere Key).
            branche: brancheResolved,
            brancheKey,
            brancheSonstiges: branche === 'sonstiges' ? brancheSonstiges.trim() : '',
            arbeitgeberDaten: {
                salonname: clientData?.salonname || clientData?.firmenname || '',
                ort: clientData?.ort || '',
                strasse: clientData?.strasse || '',
                plz: clientData?.plz || '',
                branche: brancheResolved,
                brancheKey,
            },
            mitarbeiterDaten: {
                vorname: vorname.trim(),
                nachname: nachname.trim(),
                geburtsdatum,
                geburtsort: geburtsort.trim(),
                position: position.trim(),
            },
            zeitraum: {
                eintrittsdatum,
                austrittsdatum,
                ausstellungsdatum,
            },
            aufgaben: aufgaben.trim(),
            zeugnisTyp,
            istAuszubildender,
            austrittsgrund,
        };

        if (zeugnisTyp === 'qualifiziert') {
            payload.klartextFeedback = fieserTextsack.trim();
            payload.bewertungen = {
                leistung: mapBewertungToNumber(leistungsbewertung),
                verhaltenVorgesetzte: mapBewertungToNumber(verhaltenVorgesetzte),
                verhaltenKollegen: mapBewertungToNumber(verhaltenKollegen),
            };
        }

        return payload;
    };

    useEffect(() => {
        if (!isOpen || currentStep !== 2 || generationTrigger === 0) return;

        let cancelled = false;

        const generateArbeitszeugnis = async () => {
            if (generationInFlightRef.current) return;
            generationInFlightRef.current = true;

            setIsGenerating(true);
            setApiError(false);
            setFormError('');
            setGeneratedHtml(null);

            try {
                const payload = payloadSnapshotRef.current || buildApiPayload();
                payloadSnapshotRef.current = payload;
                console.log('generatePePapersArbeitszeugnis Payload:', payload);

                if (!String(payload.branche || '').trim()) {
                    throw new Error(
                        'Branche fehlt im API-Payload. Bitte zurück zu Schritt 1 und Branche erneut wählen.'
                    );
                }

                const generateFn = httpsCallable(functions, 'generatePePapersArbeitszeugnis');
                const response = await generateFn(payload);

                if (cancelled) return;

                const data = response.data as { html?: string } | null;
                if (data?.html?.trim()) {
                    const salonHint = String(
                        clientData?.salonname || clientData?.firmenname || ''
                    ).trim();
                    setGeneratedHtml(scrubArbeitszeugnisHtml(data.html, salonHint));
                } else {
                    throw new Error('Leere API-Antwort');
                }
            } catch (error) {
                if (cancelled) return;
                logFirebaseCallableError(error, payloadSnapshotRef.current || buildApiPayload());
                setApiError(true);
                const detail = getCallableErrorMessage(error);
                setFormError(detail ? `${API_ERROR_MESSAGE} (${detail})` : API_ERROR_MESSAGE);
            } finally {
                generationInFlightRef.current = false;
                if (!cancelled) setIsGenerating(false);
            }
        };

        void generateArbeitszeugnis();

        return () => {
            cancelled = true;
        };
    // Payload-Snapshot beim Eintritt in Schritt 2 – kein Re-Fetch bei clientData-Updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, currentStep, generationTrigger]);

    const clearFieldError = (fieldId: string) => {
        setInvalidFields((prev) => {
            if (!prev.has(fieldId)) return prev;
            const next = new Set(prev);
            next.delete(fieldId);
            return next;
        });
    };

    const getInputClass = (baseClass: string, fieldId: string) =>
        `${baseClass}${invalidFields.has(fieldId) ? ` ${ERROR_INPUT_RING}` : ''}`;

    const renderFieldError = (fieldId: string) =>
        invalidFields.has(fieldId) ? (
            <p className="text-red-400 text-xs mt-1">Pflichtfeld – bitte ausfüllen.</p>
        ) : null;

    const renderDateField = (label: string, value: string, onChange: (v: string) => void, fieldId: string) => (
        <div>
            <label className={LABEL_CLASS}>{label}</label>
            <input
                type="date"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className={getInputClass(DATE_INPUT_CLASS, fieldId)}
            />
            {renderFieldError(fieldId)}
        </div>
    );

    const renderBewertungsSelect = (
        label: string,
        value: BewertungsStufe,
        onChange: (v: BewertungsStufe) => void,
        fieldId: string
    ) => (
        <div>
            <label className={LABEL_CLASS}>{label}</label>
            <select
                value={value}
                onChange={(e) => {
                    onChange(e.target.value as BewertungsStufe);
                    clearFieldError(fieldId);
                }}
                className={getInputClass(INPUT_CLASS, fieldId)}
            >
                <option value="">Bitte wählen…</option>
                {BEWERTUNGS_STUFEN.map((opt) => (
                    <option key={opt.key} value={opt.key}>
                        {opt.label}
                    </option>
                ))}
            </select>
            {renderFieldError(fieldId)}
        </div>
    );

    const renderZeugnisTypControl = () => (
        <div className="flex flex-col sm:flex-row rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black p-1 gap-1">
            {ZEUGNIS_TYP_OPTIONS.map((opt) => (
                <button
                    key={opt.key}
                    type="button"
                    onClick={() => {
                        setZeugnisTyp(opt.key);
                        setInvalidFields(new Set());
                    }}
                    className={`flex-1 min-w-0 px-2 py-2.5 rounded-md text-[9px] sm:text-[10px] font-bold uppercase tracking-normal leading-tight text-center whitespace-normal break-words transition-colors ${
                        zeugnisTyp === opt.key
                            ? 'bg-[#E6C299]/20 text-pe-accent-text dark:text-[#E6C299] border border-[#E6C299]/40'
                            : 'text-stone-500 dark:text-slate-500 hover:text-stone-700 dark:hover:text-slate-300 border border-transparent'
                    }`}
                    title={opt.label}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    );

    const dokumentTitel = istAuszubildender ? 'Ausbildungszeugnis' : 'Arbeitszeugnis';

    const validateForm = (): boolean => {
        const errors = new Set<string>();

        if (isEmpty(vorname)) errors.add('vorname');
        if (isEmpty(nachname)) errors.add('nachname');
        if (isEmpty(geburtsdatum)) errors.add('geburtsdatum');
        if (isEmpty(geburtsort)) errors.add('geburtsort');
        if (!branche) errors.add('branche');
        if (branche === 'sonstiges' && isEmpty(brancheSonstiges)) errors.add('branche_sonstiges');
        // Doppelcheck: API braucht einen nicht-leeren Branchen-String
        if (branche && isEmpty(resolveBrancheLabel(branche, brancheSonstiges))) {
            errors.add(branche === 'sonstiges' ? 'branche_sonstiges' : 'branche');
        }
        if (isEmpty(position)) errors.add('position');
        if (isEmpty(aufgaben)) errors.add('aufgaben');
        if (isEmpty(eintrittsdatum)) errors.add('eintrittsdatum');
        if (isEmpty(austrittsdatum)) errors.add('austrittsdatum');
        if (!austrittsgrund) errors.add('austrittsgrund');
        if (isEmpty(ausstellungsdatum)) errors.add('ausstellungsdatum');

        if (zeugnisTyp === 'qualifiziert') {
            if (!leistungsbewertung) errors.add('leistungsbewertung');
            if (!verhaltenVorgesetzte) errors.add('verhalten_vorgesetzte');
            if (!verhaltenKollegen) errors.add('verhalten_kollegen');
            if (isEmpty(fieserTextsack)) errors.add('fieser_textsack');
        }

        if (errors.size > 0) {
            errors.forEach((field) => console.log('Validierung fehlgeschlagen bei Feld:', field));
        }

        setInvalidFields(errors);
        return errors.size === 0;
    };

    const handleWeiter = () => {
        console.log('Submit getriggert');

        if (!validateForm()) {
            console.log('Validierung fehlgeschlagen – Submit abgebrochen.');
            const brancheOk = Boolean(branche) && !isEmpty(resolveBrancheLabel(branche, brancheSonstiges));
            setFormError(
                brancheOk
                    ? 'Bitte alle markierten Pflichtfelder ausfüllen.'
                    : 'Branche fehlt oder ist unvollständig. Bitte Branche wählen (bei „Sonstiges“ eigene Branche eintragen).'
            );
            return;
        }

        setFormError('');
        setInvalidFields(new Set());
        setApiError(false);
        const snapshot = buildApiPayload();
        if (!String(snapshot.branche || '').trim()) {
            setInvalidFields(new Set([branche === 'sonstiges' ? 'branche_sonstiges' : 'branche']));
            setFormError(
                'Branche fehlt oder ist unvollständig. Bitte Branche wählen (bei „Sonstiges“ eigene Branche eintragen).'
            );
            return;
        }
        payloadSnapshotRef.current = snapshot;
        console.log('Validierung erfolgreich – Weiterleitung zu Schritt 2.', {
            branche: snapshot.branche,
            brancheKey: snapshot.brancheKey,
            reuseExistingHtml: Boolean(generatedHtml?.trim()),
        });
        setCurrentStep(2);
        // Vorhandenen Archiv-/KI-Text behalten — nur neu generieren wenn keiner da ist
        if (!generatedHtml?.trim()) {
            setGenerationTrigger((prev) => prev + 1);
        }
    };

    const handleRegenerateKiText = () => {
        if (isGenerating || isPdfDownloading) return;
        const snapshot = buildApiPayload();
        if (!String(snapshot.branche || '').trim()) {
            setFormError(
                'Branche fehlt oder ist unvollständig. Bitte zurück zu Schritt 1 und Branche prüfen.'
            );
            return;
        }
        setFormError('');
        setApiError(false);
        payloadSnapshotRef.current = snapshot;
        setGeneratedHtml(null);
        setCurrentStep(2);
        setGenerationTrigger((prev) => prev + 1);
    };

    const handleZurueck = () => {
        setFormError('');
        setInvalidFields(new Set());
        setApiError(false);
        // KI-Text behalten — sonst ist nach „Zurück“ der Archiv-Inhalt weg
        setCurrentStep(1);
    };

    const getPrintHtml = () =>
        printContainerRef.current?.innerHTML ||
        document.getElementById('pepapers-arbeitszeugnis-print')?.innerHTML ||
        '';

    const isActionLocked = !disclaimerAccepted;

    const getActionButtonClass = (baseClass: string) =>
        `${baseClass} ${isActionLocked ? 'opacity-50 cursor-not-allowed' : ''}`;

    const handleArchive = async (asDraft = false) => {
        if (isSaving) return;
        if (!asDraft && isActionLocked) return;

        setIsSaving(true);
        setArchiveFeedback('idle');

        const salonName =
            String(clientData?.salonname || clientData?.firmenname || '').trim() || 'Unbekannter Salon';

        // Immer den reinen KI-Fließtext speichern (nicht die Druck-Shell)
        const htmlToStore = generatedHtml?.trim()
            ? scrubArbeitszeugnisHtml(generatedHtml, salonName)
            : extractKiHtmlFromStored(
                  printContainerRef.current?.innerHTML ||
                      document.getElementById('pepapers-arbeitszeugnis-print')?.innerHTML ||
                      '',
                  salonName
              );

        const mitarbeiterName = `${vorname.trim()} ${nachname.trim()}`.trim();

        try {
            const id = await savePePapersArchiveDoc({
                id: archiveDocId,
                documentType: 'arbeitszeugnis',
                status: asDraft ? 'entwurf' : 'final',
                salonName,
                mitarbeiterName,
                confirmedDisclaimer: asDraft ? disclaimerAccepted : true,
                // Mit vorhandenem KI-Text immer als Schritt 2 speichern, damit Wiederöffnen die Vorschau zeigt
                currentStep: htmlToStore ? 2 : currentStep,
                payload: {
                    zeugnisTyp,
                    istAuszubildender,
                    selectedTeamEmployeeId: selectedTeamEmployeeId || null,
                    vorname: vorname.trim(),
                    nachname: nachname.trim(),
                    geburtsdatum,
                    geburtsort: geburtsort.trim(),
                    branche,
                    brancheSonstiges: brancheSonstiges.trim(),
                    position: position.trim(),
                    aufgaben: aufgaben.trim(),
                    eintrittsdatum,
                    austrittsdatum,
                    austrittsgrund,
                    ausstellungsdatum,
                    leistungsbewertung,
                    verhaltenVorgesetzte,
                    verhaltenKollegen,
                    fieserTextsack: fieserTextsack.trim(),
                    generatedHtml: htmlToStore,
                },
            });
            setArchiveDocId(id);
            setArchiveFeedback(asDraft ? 'draft' : 'success');
            onArchived?.();
            if (asDraft) {
                setTimeout(() => {
                    onClose();
                }, 600);
            } else {
                setTimeout(() => setArchiveFeedback('idle'), 3000);
            }
        } catch (error) {
            console.error('PEPapers Arbeitszeugnis Archivierung fehlgeschlagen:', error);
            setArchiveFeedback('error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDownloadPdf = async () => {
        if (!generatedHtml || isPdfDownloading || isGenerating || isActionLocked) return;
        const html = getPrintHtml();
        if (!html.trim()) {
            window.alert('Dokumentinhalt fehlt — bitte Vorschau prüfen.');
            return;
        }
        setIsPdfDownloading(true);
        try {
            const dateIso = new Date().toISOString().slice(0, 10);
            await downloadHtmlAsPdf({
                html,
                filename: `${dokumentTitel}_${dateIso}.pdf`,
                scrubMode: 'minimal',
                // Keine künstlichen Print-Breaks aus KI-HTML → kein „Satz mitten auf der Seite“
                splitPrintBreaks: false,
                capturePadPx: 0,
                letterheadOnBreaks: false,
                extraCss: ZEUGNIS_PDF_CSS,
            });
        } catch (err: unknown) {
            console.error('PEPapers Arbeitszeugnis PDF-Download fehlgeschlagen:', err);
            const message = err instanceof Error ? err.message : 'Unbekannter Fehler';
            window.alert('PDF konnte nicht heruntergeladen werden: ' + message);
        } finally {
            setIsPdfDownloading(false);
        }
    };

    const handlePrintIsolated = () => {
        if (!generatedHtml || isPdfDownloading || isActionLocked) return;

        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0px';
        iframe.style.height = '0px';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);

        const printWindow = iframe.contentWindow;
        if (!printWindow) {
            document.body.removeChild(iframe);
            return;
        }

        const printContent = getPrintHtml();

        const doc = printWindow.document;
        doc.open();
        doc.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>${dokumentTitel}</title>
      <style>${ZEUGNIS_PRINT_CSS}</style>
    </head>
    <body>${printContent}</body>
    </html>
  `);
        doc.close();

        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            setTimeout(() => {
                if (iframe.parentNode) document.body.removeChild(iframe);
            }, 1000);
        }, 500);
    };

    const renderStep2Content = () => {
        if (isGenerating) {
            return (
                <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                    <Loader2 className="w-10 h-10 text-pe-accent-text dark:text-[#E6C299] animate-spin mb-4" />
                    <p className="text-sm text-pe-muted dark:text-slate-300 leading-relaxed max-w-sm">
                        KI analysiert Klartext und generiert Zeugnis-Code... Bitte warten
                    </p>
                </div>
            );
        }

        if (apiError || !generatedHtml) {
            return null;
        }

        const salonName =
            String(clientData?.salonname || clientData?.firmenname || '').trim() || 'Arbeitgeber';
        const scrubbedHtml = scrubArbeitszeugnisHtml(generatedHtml, salonName);
        const ortDatum = [
            String(clientData?.ort || '').trim() || '____________',
            ausstellungsdatum
                ? (() => {
                      const [y, m, d] = ausstellungsdatum.split('-');
                      return y && m && d ? `${d}.${m}.${y}` : ausstellungsdatum;
                  })()
                : '____________',
        ].join(', ');

        return (
            <div className="w-full min-h-0 pr-2">
                <div ref={printContainerRef} id="pepapers-arbeitszeugnis-print" className="w-full h-auto overflow-visible">
                    <style>{ZEUGNIS_PDF_CSS}</style>
                    <div className={A4_PREVIEW_PAGE_CLASS}>
                        <div className="pdf-letterhead text-center">
                            <h1>{dokumentTitel}</h1>
                            <h2>PreisExpert · PEPapers</h2>
                        </div>
                        <div
                            className={KI_HTML_PREVIEW_CLASS}
                            dangerouslySetInnerHTML={{ __html: scrubbedHtml }}
                        />
                        <table
                            className="az-sig-table"
                            style={{
                                width: '100%',
                                marginTop: '24px',
                                borderCollapse: 'collapse',
                                tableLayout: 'fixed',
                            }}
                        >
                            <tbody>
                                <tr>
                                    <td style={{ width: '50%', paddingRight: '20px', verticalAlign: 'top' }}>
                                        <p style={{ margin: 0, fontSize: '10.5px', color: '#475569' }}>
                                            Ort, Datum: {ortDatum}
                                        </p>
                                        <div style={{ height: '36px' }} aria-hidden="true" />
                                        <div
                                            className="az-sig-line-soft"
                                            style={{
                                                width: '200px',
                                                maxWidth: '100%',
                                                borderBottom: '2px solid #1e293b',
                                                marginBottom: '6px',
                                            }}
                                            aria-hidden="true"
                                        />
                                        <p style={{ margin: 0, fontSize: '11px', fontWeight: 600, color: '#0f172a' }}>
                                            Unterschrift Arbeitgeber/in
                                        </p>
                                        <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>{salonName}</p>
                                    </td>
                                    <td style={{ width: '50%', paddingLeft: '20px', verticalAlign: 'top' }}>
                                        <p style={{ margin: 0, minHeight: '16px', fontSize: '10.5px', color: '#475569' }}>
                                            &nbsp;
                                        </p>
                                        <div style={{ height: '36px' }} aria-hidden="true" />
                                        <div
                                            className="az-sig-line-soft"
                                            style={{
                                                width: '200px',
                                                maxWidth: '100%',
                                                borderBottom: '2px solid #1e293b',
                                                marginBottom: '6px',
                                            }}
                                            aria-hidden="true"
                                        />
                                        <p style={{ margin: 0, fontSize: '11px', fontWeight: 600, color: '#0f172a' }}>
                                            Firmenstempel
                                        </p>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    if (!isOpen) return null;

    const modalWidthClass = currentStep === 2 ? 'max-w-4xl' : 'max-w-lg';

    return createPortal(
        <>
            <style>{`
                input[type="date"].arbeitszeugnis-date-input::-webkit-calendar-picker-indicator {
                    filter: invert(1) !important;
                    cursor: pointer !important;
                    display: block !important;
                    opacity: 1 !important;
                }
            `}</style>
            <div
                className="fixed inset-0 bg-stone-900/70 dark:bg-black/90 z-[100000] backdrop-blur-md"
                onClick={handleClose}
                aria-hidden="true"
            />
            <div className="fixed inset-x-0 top-[8.5rem] md:top-36 bottom-0 z-[100001] flex items-start justify-center overflow-hidden p-4 pb-8 font-sans">
                <div
                    className={`bg-pe-card dark:bg-[#111] rounded-2xl shadow-[0_0_40px_rgba(230,194,153,0.1)] border border-[#E6C299]/30 w-full ${modalWidthClass} max-h-[calc(100vh-9.5rem)] md:max-h-[calc(100vh-10rem)] flex min-h-0 flex-col justify-between overflow-hidden relative transition-all duration-200 my-2`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        type="button"
                        onClick={handleClose}
                        className="absolute top-4 right-4 text-pe-muted dark:text-slate-400 hover:text-pe-text dark:hover:text-white bg-pe-elevated dark:bg-[#1a1a1a] hover:bg-pe-hover dark:hover:bg-[#222] rounded-lg p-2 transition-colors border border-pe-border-strong dark:border-[#333] z-[60]"
                        aria-label="Schließen"
                    >
                        <X className="w-4 h-4" />
                    </button>

                    <div className="bg-pe-elevated dark:bg-[#111622] z-50 shrink-0 border-b border-pe-border dark:border-[#222]">
                        <div className="px-6 pt-10 pb-4">
                            <h3 className="text-lg font-bold text-pe-accent-text dark:text-[#E6C299] text-center uppercase tracking-widest pr-8">
                                {dokumentTitel}
                            </h3>
                            <p className="text-center text-xs text-pe-muted dark:text-slate-400 uppercase tracking-widest mt-2">
                                Schritt {currentStep} von 2: {STEP_LABELS[currentStep]}
                            </p>
                        </div>
                    </div>

                    <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 custom-scrollbar">
                        {currentStep === 1 && (
                            <div className="space-y-6">
                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 mb-3 border-b border-pe-border dark:border-[#222] pb-2">
                                        Zeugnis-Typ
                                    </p>
                                    {renderZeugnisTypControl()}
                                    <label className="mt-3 flex items-center gap-2.5 cursor-pointer select-none rounded-lg border border-pe-border dark:border-slate-700/80 bg-stone-100/60 dark:bg-black/40 px-3 py-2.5">
                                        <input
                                            type="checkbox"
                                            checked={istAuszubildender}
                                            onChange={(e) => setIstAuszubildender(e.target.checked)}
                                            className="h-4 w-4 rounded border-pe-border-strong dark:border-slate-600 bg-pe-input dark:bg-black text-pe-accent-text dark:text-[#E6C299] focus:ring-[#E6C299]/40 focus:ring-offset-0"
                                        />
                                        <span className="text-xs font-semibold text-pe-muted dark:text-slate-300">
                                            Für Auszubildende(n)
                                        </span>
                                    </label>
                                    <p className="text-xs text-stone-500 dark:text-slate-500 mt-2 leading-relaxed">
                                        {zeugnisTyp === 'einfach'
                                            ? 'Einfaches Zeugnis: Stammdaten, Beschäftigungszeitraum und Aufgabenbeschreibung.'
                                            : 'Qualifiziertes Zeugnis: Zusätzlich Bewertungsblock und Klartext-Maske für die KI-Übersetzung.'}
                                        {istAuszubildender ? ' Dokumenttitel: Ausbildungszeugnis.' : ''}
                                    </p>
                                </div>

                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 mb-3 border-b border-pe-border dark:border-[#222] pb-2">
                                        Stammdaten
                                    </p>
                                    <div className="space-y-4">
                                        <div>
                                            <label className={LABEL_CLASS}>Aus Team wählen (optional)</label>
                                            <select
                                                value={selectedTeamEmployeeId}
                                                onChange={(e) => handleTeamEmployeeSelect(e.target.value)}
                                                className={INPUT_CLASS}
                                            >
                                                <option value="">— Manuell eintragen / Ex-Mitarbeiter —</option>
                                                {teamEmployees.map((emp) => (
                                                    <option key={`${emp.pool}-${emp.id}`} value={emp.id}>
                                                        {emp.name}
                                                        {emp.pool === 'azubi'
                                                            ? ' (Azubi/Assistenz)'
                                                            : emp.pool === 'unproduktiv'
                                                              ? ' (unproduktiv)'
                                                              : ''}
                                                    </option>
                                                ))}
                                            </select>
                                            {teamEmployees.length === 0 ? (
                                                <p className="mt-1 text-[10px] text-pe-muted dark:text-slate-500">
                                                    Keine Personaldaten hinterlegt – Vor- und Nachname manuell
                                                    eintragen.
                                                </p>
                                            ) : (
                                                <p className="mt-1 text-[10px] text-pe-muted dark:text-slate-500">
                                                    Auswahl füllt den Namen vor; für Ex-Mitarbeiter einfach manuell
                                                    lassen. Felder bleiben danach editierbar.
                                                </p>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className={LABEL_CLASS}>Vorname (Mitarbeiter)</label>
                                                <input
                                                    type="text"
                                                    value={vorname}
                                                    onChange={(e) => {
                                                        setVorname(e.target.value);
                                                        clearFieldError('vorname');
                                                    }}
                                                    className={getInputClass(INPUT_CLASS, 'vorname')}
                                                />
                                                {renderFieldError('vorname')}
                                            </div>
                                            <div>
                                                <label className={LABEL_CLASS}>Nachname (Mitarbeiter)</label>
                                                <input
                                                    type="text"
                                                    value={nachname}
                                                    onChange={(e) => {
                                                        setNachname(e.target.value);
                                                        clearFieldError('nachname');
                                                    }}
                                                    className={getInputClass(INPUT_CLASS, 'nachname')}
                                                />
                                                {renderFieldError('nachname')}
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            {renderDateField('Geburtsdatum', geburtsdatum, (v) => {
                                                setGeburtsdatum(v);
                                                clearFieldError('geburtsdatum');
                                            }, 'geburtsdatum')}
                                            <div>
                                                <label className={LABEL_CLASS}>Geburtsort</label>
                                                <input
                                                    type="text"
                                                    value={geburtsort}
                                                    onChange={(e) => {
                                                        setGeburtsort(e.target.value);
                                                        clearFieldError('geburtsort');
                                                    }}
                                                    placeholder="z.B. Berlin"
                                                    className={getInputClass(INPUT_CLASS, 'geburtsort')}
                                                />
                                                {renderFieldError('geburtsort')}
                                            </div>
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Branche</label>
                                            <select
                                                value={branche}
                                                onChange={(e) => {
                                                    const next = e.target.value as Branche;
                                                    setBranche(next);
                                                    if (next !== 'sonstiges') {
                                                        setBrancheSonstiges('');
                                                        clearFieldError('branche_sonstiges');
                                                    }
                                                    clearFieldError('branche');
                                                }}
                                                className={getInputClass(INPUT_CLASS, 'branche')}
                                            >
                                                <option value="">Bitte wählen…</option>
                                                {BRANCHE_OPTIONS.map((opt) => (
                                                    <option key={opt.key} value={opt.key}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </select>
                                            {renderFieldError('branche')}
                                            {branche === 'sonstiges' && (
                                                <div className="mt-3">
                                                    <label className={LABEL_CLASS}>Eigene Branche</label>
                                                    <input
                                                        type="text"
                                                        value={brancheSonstiges}
                                                        onChange={(e) => {
                                                            setBrancheSonstiges(e.target.value);
                                                            clearFieldError('branche_sonstiges');
                                                        }}
                                                        placeholder="z. B. Tattoo-Studio, Massagepraxis"
                                                        className={getInputClass(INPUT_CLASS, 'branche_sonstiges')}
                                                    />
                                                    {renderFieldError('branche_sonstiges')}
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>
                                                Position / Genaue Berufsbezeichnung
                                            </label>
                                            <input
                                                type="text"
                                                value={position}
                                                onChange={(e) => {
                                                    setPosition(e.target.value);
                                                    clearFieldError('position');
                                                }}
                                                placeholder="z. B. Friseurmeister/in, Top-Stylist/in"
                                                className={getInputClass(INPUT_CLASS, 'position')}
                                            />
                                            {renderFieldError('position')}
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>
                                                Detaillierte Aufgaben und Tätigkeiten (Stichpunkte)
                                            </label>
                                            <textarea
                                                value={aufgaben}
                                                onChange={(e) => {
                                                    setAufgaben(e.target.value);
                                                    clearFieldError('aufgaben');
                                                }}
                                                placeholder="• Kundenberatung und -betreuung&#10;• Haarschnitte, Färben und Styling&#10;• Salonhygiene und Warenbestellung"
                                                className={getInputClass(TEXTAREA_CLASS, 'aufgaben')}
                                            />
                                            {renderFieldError('aufgaben')}
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 mb-3 border-b border-pe-border dark:border-[#222] pb-2">
                                        Beschäftigungszeitraum & Ausstellung
                                    </p>
                                    <div className="space-y-4">
                                        {renderDateField('Eintrittsdatum', eintrittsdatum, (v) => {
                                            setEintrittsdatum(v);
                                            clearFieldError('eintrittsdatum');
                                        }, 'eintrittsdatum')}
                                        {renderDateField('Austrittsdatum', austrittsdatum, (v) => {
                                            setAustrittsdatum(v);
                                            clearFieldError('austrittsdatum');
                                        }, 'austrittsdatum')}
                                        <div>
                                            <label className={LABEL_CLASS}>Art der Beendigung / Austrittsgrund</label>
                                            <select
                                                value={austrittsgrund}
                                                onChange={(e) => {
                                                    setAustrittsgrund(e.target.value as Austrittsgrund);
                                                    clearFieldError('austrittsgrund');
                                                }}
                                                className={getInputClass(INPUT_CLASS, 'austrittsgrund')}
                                            >
                                                <option value="">Bitte wählen…</option>
                                                {AUSTRITTSGRUND_OPTIONS.map((opt) => (
                                                    <option key={opt.key} value={opt.key}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </select>
                                            {renderFieldError('austrittsgrund')}
                                        </div>
                                        {renderDateField(
                                            'Ausstellungsdatum des Zeugnisses',
                                            ausstellungsdatum,
                                            (v) => {
                                                setAusstellungsdatum(v);
                                                clearFieldError('ausstellungsdatum');
                                            },
                                            'ausstellungsdatum'
                                        )}
                                    </div>
                                </div>

                                {zeugnisTyp === 'qualifiziert' && (
                                    <div>
                                        <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 mb-3 border-b border-pe-border dark:border-[#222] pb-2">
                                            Bewertungsblock
                                        </p>
                                        <div className="space-y-4">
                                            {renderBewertungsSelect(
                                                'Leistungsbeurteilung',
                                                leistungsbewertung,
                                                setLeistungsbewertung,
                                                'leistungsbewertung'
                                            )}
                                            {renderBewertungsSelect(
                                                'Verhalten gegenüber Vorgesetzten',
                                                verhaltenVorgesetzte,
                                                setVerhaltenVorgesetzte,
                                                'verhalten_vorgesetzte'
                                            )}
                                            {renderBewertungsSelect(
                                                'Verhalten gegenüber Kollegen & Kunden',
                                                verhaltenKollegen,
                                                setVerhaltenKollegen,
                                                'verhalten_kollegen'
                                            )}

                                            <div className="rounded-lg border border-[#E6C299]/20 bg-[#E6C299]/5 p-4">
                                                <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/90 mb-3">
                                                    Klartext-Übersetzungsmaske
                                                </p>
                                                <p className="text-xs text-pe-muted dark:text-slate-400 leading-relaxed mb-4">
                                                    {KI_KLARTEXT_HINWEIS}
                                                </p>
                                                <label className={LABEL_CLASS}>
                                                    Fieser Textsack (ehrliche Bewertung in Klartext)
                                                </label>
                                                <textarea
                                                    value={fieserTextsack}
                                                    onChange={(e) => {
                                                        setFieserTextsack(e.target.value);
                                                        clearFieldError('fieser_textsack');
                                                    }}
                                                    placeholder="Schreib hier roh und ehrlich, was du wirklich denkst – die KI übersetzt es später in rechtssichere Zeugnissprache."
                                                    className={getInputClass(FIESER_TEXTSACK_CLASS, 'fieser_textsack')}
                                                />
                                                {renderFieldError('fieser_textsack')}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {currentStep === 2 && renderStep2Content()}
                    </div>

                    {currentStep === 2 && (
                        <PePapersDisclaimerGate
                            accepted={disclaimerAccepted}
                            onAcceptedChange={setDisclaimerAccepted}
                        />
                    )}

                    <div className="px-6 py-4 border-t border-pe-border dark:border-[#222] shrink-0">
                        {formError && (
                            <p className="text-red-400 text-xs text-center mb-3 leading-relaxed">{formError}</p>
                        )}
                        {archiveFeedback === 'error' && (
                            <p className="text-red-400 text-xs text-center mb-3">
                                Archivieren fehlgeschlagen. Bitte erneut versuchen.
                            </p>
                        )}
                        <div className="flex flex-wrap gap-3">
                            {currentStep > 1 && (
                                <button
                                    type="button"
                                    onClick={handleZurueck}
                                    disabled={isGenerating}
                                    className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Zurück
                                </button>
                            )}
                            {currentStep === 1 ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(true)}
                                        disabled={isSaving || isGenerating}
                                        className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors disabled:opacity-50"
                                    >
                                        {isSaving && archiveFeedback !== 'draft' ? 'Speichert...' : 'Später fortsetzen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleWeiter}
                                        className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest bg-[#E6C299] hover:bg-[#d9b183] dark:hover:bg-white text-black transition-colors"
                                    >
                                        Dokument erstellen
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={handleRegenerateKiText}
                                        disabled={isSaving || isGenerating || isPdfDownloading}
                                        className="flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors disabled:opacity-50"
                                    >
                                        {isGenerating ? 'Generiert...' : 'Text neu generieren'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(true)}
                                        disabled={isSaving || isGenerating}
                                        className="flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors disabled:opacity-50"
                                    >
                                        {archiveFeedback === 'draft' ? 'Gespeichert! ✓' : 'Später fortsetzen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(false)}
                                        disabled={isActionLocked || isSaving || isGenerating}
                                        className={getActionButtonClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-pe-card dark:bg-[#15171e] hover:bg-pe-hover dark:hover:bg-[#1a1d24] border-[#E6C299]/40 text-pe-accent-text dark:text-[#E6C299] transition-colors'
                                        )}
                                    >
                                        {isSaving && archiveFeedback !== 'draft'
                                            ? 'Speichert...'
                                            : archiveFeedback === 'success'
                                              ? 'Gespeichert! ✓'
                                              : 'Im Archiv speichern'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleDownloadPdf()}
                                        disabled={
                                            isActionLocked ||
                                            isGenerating ||
                                            isPdfDownloading ||
                                            !generatedHtml
                                        }
                                        className={getActionButtonClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest bg-gradient-to-b from-[#E6C299] to-[#b48a56] hover:from-[#f0d3b3] hover:to-[#cfa674] text-black transition-colors'
                                        )}
                                    >
                                        {isPdfDownloading ? 'PDF wird erstellt...' : 'Als PDF herunterladen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handlePrintIsolated}
                                        disabled={
                                            isActionLocked ||
                                            isGenerating ||
                                            isPdfDownloading ||
                                            !generatedHtml
                                        }
                                        className={getActionButtonClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border border-[#E6C299]/40 text-pe-accent-text dark:text-[#E6C299] transition-colors'
                                        )}
                                    >
                                        Drucken
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

export default ArbeitszeugnisModal;
